import type { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';
import SvgClipCopy from 'components/svg/svgClipCopy';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';

interface ContactRowActionsProps {
    rowKey: string;
    disabled: boolean;
    editing: boolean;
    canEdit: boolean;
    onStartEdit: () => void;
    onCancelEdit: () => void;
    onCopy: () => void;
    onRemove: () => void;
}

const ContactRowActions = ({
    rowKey,
    disabled,
    editing,
    canEdit,
    onStartEdit,
    onCancelEdit,
    onCopy,
    onRemove,
}: ContactRowActionsProps) => {
    const { t } = useTranslation(['Clients', 'common']);

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            disabled={disabled}
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {canEdit
                ? editing
                    ? renderMenuItem(
                          <SolarIcon name="x-lg" />,
                          t('common:buttons.cancel'),
                          t('contacts.cancelHint'),
                          onCancelEdit,
                      )
                    : renderMenuItem(
                          <SvgEdit />,
                          t('contacts.edit'),
                          t('contacts.editHint'),
                          onStartEdit,
                      )
                : null}

            {renderMenuItem(<SvgClipCopy />, t('contacts.copy'), t('contacts.copyHint'), onCopy)}

            {canEdit ? (
                <>
                    <Dropdown.Divider className="my-2" />
                    {renderMenuItem(
                        <SvgTrash />,
                        t('common:buttons.delete'),
                        t('contacts.deleteHint'),
                        onRemove,
                        { danger: true },
                    )}
                </>
            ) : null}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                disabled={disabled}
                id={`dropdown-client-contact-${rowKey}`}
            >
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>
            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ContactRowActions;
